import { useEffect } from 'react';
import {
  backgrounds,
  defaultVirtualBackground,
} from '../../components/media/virtualBackground/constants';
import {
  getVirtualBackgroundFilterFromLocalStorage,
  getVirtualBackgroundIndexFromLocalStorage,
  setVirtualBackgroundToLocalStorage,
} from '../../components/media/virtualBackground';
import { useGetCustomVirtualBackgroundsQuery } from '../../services/branding/operations.enhanced';
import { media } from '../../webrtc/media';
import { useGetUserId } from '../useCredentials';
import { useLocalMedia } from '../useLocalMedia';
import { useAllVirtualBackgrounds } from './useAllVirtualBackgrounds';
import { useIsVirtualBackgroundEnabled } from './useIsVirtualBackgroundEnabled';

/**
 * Indices 100–102; must match useBrandedVirtualBackgrounds.
 */
const isBrandedVirtualBackgroundIndex = (index: number) => index >= 100;

/**
 * Re-applies the stored virtual background after tenant virtual backgrounds finish loading.
 * On F5, media often starts before branded rows are in the list.
 */
export const useSyncStoredVirtualBackground = (): void => {
  const userId = useGetUserId();
  const isEnabled = useIsVirtualBackgroundEnabled();
  const allBackgrounds = useAllVirtualBackgrounds();
  const { hasVideo } = useLocalMedia();
  const { isLoading: isCustomVirtualBackgroundsLoading } =
    useGetCustomVirtualBackgroundsQuery();

  useEffect(() => {
    if (!isEnabled || !hasVideo) {
      return;
    }

    const storedIndex = getVirtualBackgroundIndexFromLocalStorage(userId);
    const isStoredInStaticBackgrounds = backgrounds.some(
      (background) => background.index === storedIndex,
    );

    if (
      isBrandedVirtualBackgroundIndex(storedIndex) &&
      !isStoredInStaticBackgrounds &&
      isCustomVirtualBackgroundsLoading
    ) {
      return;
    }

    // A branded background that was applied can disappear once its source is
    // removed (e.g. the clinic resets its logo). Clear the stale filter and
    // reset the selection instead of leaving the old background applied.
    const storedBackgroundExists = allBackgrounds.some(
      (background) => background.index === storedIndex,
    );

    if (!storedBackgroundExists) {
      if (userId !== undefined) {
        setVirtualBackgroundToLocalStorage(defaultVirtualBackground, userId);
      }
      void media.applyVirtualBackground(null);
      return;
    }

    const filter = getVirtualBackgroundFilterFromLocalStorage(
      userId,
      allBackgrounds,
    );

    if (!filter) {
      return;
    }

    void media.applyVirtualBackground(filter);
  }, [
    userId,
    isEnabled,
    hasVideo,
    isCustomVirtualBackgroundsLoading,
    allBackgrounds,
  ]);
};
